import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { formatPriceWithMark } from '@this/src/util/price/price.util';
import {
  PROPOSAL_BUNDLE_CATEGORY,
  PROPOSAL_BUNDLE_STATE,
  type PreferredTransportType,
  type ProposalBundle,
  type ProposalItem
} from '../../types/proposalBundles';
import { formatTransportRouteInfo } from '../../utils/routeInfoFormatter';
import { getForeignFlightDurationHeaderLabel } from './utils';
import { ForeignFlightLegCardRow } from './ForeignFlightLegCardRow';
import {
  FOREIGN_FLIGHT_COL_WIDTHS,
  getForeignFlightProposalTransportTableStyles
} from '../proposal/ForeignFlightProposalTransportTable';

type Props = {
  stepLabel: string;
  title: string;
  bundle: ProposalBundle;
  items: ProposalItem[];
  includeTax: boolean;
  onSelect?: (bundleId: number, itemId: number) => void;
  onClose?: (bundleId: number) => void;
  disabled?: boolean;
  selectionDisabled?: boolean;
  lockSelection?: boolean;
  emptyMessage?: string | null;
};

const computeShowRoundTripAirPriceHeader = (
  displayTransportType: PreferredTransportType | null,
  bundle: Pick<ProposalBundle, 'category' | 'hasForeignFlight' | 'hasForeignFlightRoundTrip'>
): boolean => {
  const hasForeignFlight = Boolean(bundle.hasForeignFlight);
  const hasForeignFlightRoundTrip = Boolean(bundle.hasForeignFlightRoundTrip);
  const isOutwardOrHomeward =
    bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD || bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD;
  const isForeignFlightOneWayOnly = hasForeignFlight && !hasForeignFlightRoundTrip;
  return displayTransportType === 'air' && isOutwardOrHomeward && !isForeignFlightOneWayOnly;
};

const formatForeignFlightLegRoundTripPriceBand = (
  legTitle: string,
  items: Array<Pick<ProposalItem, 'price' | 'priceWithTax'>>,
  includeTax: boolean
): string | null => {
  const pickNumericPrice = (
    item: Pick<ProposalItem, 'price' | 'priceWithTax'>,
    _includeTax: boolean
  ): number | null => {
    const targetPrice = _includeTax ? item.priceWithTax : item.price;
    if (targetPrice != null && Number.isFinite(targetPrice)) {
      return targetPrice;
    }
    return null;
  };

  const prices = items.map(item => pickNumericPrice(item, includeTax)).filter((n): n is number => n != null);
  if (prices.length === 0) {
    return null;
  }
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const minText = formatPriceWithMark(min);
  const maxText = formatPriceWithMark(max);
  if (!minText || !maxText) {
    return null;
  }
  const label = `「${legTitle}」候補の往復合計料金`;
  if (min === max) {
    return `${label}: ${minText}`;
  }
  return `${label}: ${minText} 〜 ${maxText}`;
};

const ForeignFlightLegCard = ({
  stepLabel,
  title,
  bundle,
  items,
  includeTax,
  onSelect,
  onClose,
  disabled = false,
  selectionDisabled = false,
  lockSelection = false,
  emptyMessage = null
}: Props) => {
  const { theme } = useAITTheme();
  const isRejected = bundle.state === PROPOSAL_BUNDLE_STATE.REJECTED;
  const styles = useMemo(() => getStyles(theme, isRejected), [theme, isRejected]);
  const routeCaption = useMemo(
    () => formatTransportRouteInfo(bundle.origin, bundle.destination, bundle.departureDate),
    [bundle.origin, bundle.destination, bundle.departureDate]
  );
  const priceBand = useMemo(
    () => formatForeignFlightLegRoundTripPriceBand(title, items, includeTax),
    [title, items, includeTax]
  );
  const hasRoundTripPair = bundle.hasForeignFlightRoundTrip === true;
  const tableStyles = useMemo(() => getForeignFlightProposalTransportTableStyles(theme, false), [theme]);
  const durationHeader = getForeignFlightDurationHeaderLabel(hasRoundTripPair, bundle.category);
  const showRoundTripAirPriceHeader = computeShowRoundTripAirPriceHeader('air', bundle);
  const mergedTableStyle = useMemo(
    () => ({ ...tableStyles.table, ...tableStyles.frTable }),
    [tableStyles.frTable, tableStyles.table]
  );

  const handleClose = () => {
    onClose?.(bundle.id);
  };

  const handleSelect = (itemId: number) => {
    onSelect?.(bundle.id, itemId);
  };

  const selectBlocked = disabled || selectionDisabled;
  const bundleHasSelection = items.some(i => i.selected);

  const tableColSpan = 9;

  return (
    <div style={styles.card}>
      {!isRejected && (
        <button
          type="button"
          style={{
            ...styles.closeButton,
            ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {})
          }}
          onClick={handleClose}
          disabled={disabled}
        >
          ×
        </button>
      )}

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.stepBadge}>{stepLabel}</span>
          <span style={styles.productBadge}>海外航空</span>
          <span style={styles.categoryLabel}>
            <span style={styles.categoryIcon}>
              <img src="/images/plane.png" alt="" style={{ width: '16px', height: '16px' }} />
            </span>
            <span>{title}</span>
            {routeCaption}
          </span>
        </div>
        {isRejected && <span style={styles.rejectedLabel}>予約しない</span>}
      </div>

      {items.length > 0 && priceBand && (
        <div style={styles.bandBlock} role="status">
          <div style={styles.bandRow}>{priceBand}</div>
          <div style={styles.bandCaption}>表の「料金（往復）」は、往路＋復路を含む同一セットの合計です。</div>
        </div>
      )}

      <div style={tableStyles.tableScroll}>
        <table style={mergedTableStyle}>
          <colgroup>
            {FOREIGN_FLIGHT_COL_WIDTHS.map((w, i) => (
              <col key={i} style={{ width: w }} />
            ))}
          </colgroup>
          <thead>
            <tr style={tableStyles.tableHeaderRow}>
              <th style={tableStyles.frThNarrow}> </th>
              <th style={tableStyles.frTh}>出発→到着</th>
              <th style={tableStyles.frTh}>{durationHeader}</th>
              <th style={tableStyles.frTh}>区間</th>
              <th style={tableStyles.frTh}>航空会社</th>
              <th style={tableStyles.frTh}>便名</th>
              <th style={tableStyles.frTh}>座席</th>
              <th style={tableStyles.frThCenter}>変更可否</th>
              <th style={tableStyles.frThPrice}>{showRoundTripAirPriceHeader ? '料金（往復）' : '料金'}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && emptyMessage && (
              <tr>
                <td colSpan={tableColSpan} style={styles.emptyCell}>
                  {emptyMessage}
                </td>
              </tr>
            )}
            {items.map((item, index) => (
              <ForeignFlightLegCardRow
                key={item.id}
                item={item}
                index={index}
                includeTax={includeTax}
                onSelect={() => handleSelect(item.id)}
                theme={theme}
                disabled={selectBlocked || (lockSelection && bundleHasSelection && !(item.selected ?? false))}
                hasRoundTripPair={hasRoundTripPair}
                bundleCategory={bundle.category}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const getStyles = (theme: AITTheme, isRejected: boolean) => ({
  card: {
    backgroundColor: theme.whiteColor,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    border: `2px solid ${isRejected ? theme.grayBorderColor : theme.linkColor}`,
    position: 'relative' as const,
    opacity: isRejected ? 0.6 : 1,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box' as const
  },
  closeButton: {
    position: 'absolute' as const,
    top: '12px',
    right: '12px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontSize: '18px',
    fontWeight: 300 as const,
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  header: {
    fontWeight: 700 as const,
    color: isRejected ? theme.grayTextColor : theme.linkColor,
    marginBottom: '12px',
    fontSize: '17px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap' as const
  },
  stepBadge: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    padding: '4px 10px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 700 as const
  },
  productBadge: {
    backgroundColor: isRejected ? theme.grayTextColor : theme.linkColor,
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const
  },
  categoryLabel: {
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    textDecoration: isRejected ? 'line-through' : 'none'
  },
  categoryIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1
  },
  rejectedLabel: {
    backgroundColor: '#f5f5f5',
    color: theme.grayTextColor,
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const
  },
  bandBlock: {
    marginBottom: '12px'
  },
  bandRow: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor,
    lineHeight: 1.5
  },
  bandCaption: {
    fontSize: '11px',
    color: '#555555',
    marginTop: '6px',
    lineHeight: 1.5
  },
  emptyCell: {
    padding: '20px',
    textAlign: 'center' as const,
    fontSize: '13px',
    color: '#666666'
  }
});

export default ForeignFlightLegCard;
